/**
 * General Settings Tab - Organization details and notification defaults
 * Uses live organization query for name/contact; notification toggles are mock/static UI.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Save } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../stores/authStore';
import organizationService, {
  Organization,
  UpdateOrganizationRequest,
  NotificationPreferences,
} from '../../services/api/organizationService';

// ---------------------------------------------------------------------------
// Shared layout primitives
// ---------------------------------------------------------------------------

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const ContentCard = styled(Card)``;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 44px;
  display: flex;
  align-items: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const SuccessMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  color: ${({ theme }) => theme.colors.status.error.text};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const ToggleLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ToggleTrack = styled.button<{ $on: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  background: ${({ theme, $on }) =>
    $on ? theme.colors.primary.base : theme.colors.borders.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.base};
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ToggleThumb = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $on }) => ($on ? '22px' : '3px')};
  width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.text.white};
  transition: left ${({ theme }) => theme.transitions.base};
`;

interface ToggleSwitchProps {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}

const ToggleSwitch = ({ label, value, onChange, disabled }: ToggleSwitchProps) => (
  <ToggleRow>
    <ToggleLabel>{label}</ToggleLabel>
    <ToggleTrack
      type="button"
      $on={value}
      onClick={() => !disabled && onChange(!value)}
      aria-pressed={value}
      aria-label={label}
      disabled={disabled}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
    >
      <ToggleThumb $on={value} />
    </ToggleTrack>
  </ToggleRow>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GeneralSettingsTab = () => {
  const { user, hasPermission, isSuperAdmin } = useAuthStore();
  const queryClient = useQueryClient();

  const canEdit = !isSuperAdmin() && hasPermission('org:settings:manage');
  const canEditNotifications = !isSuperAdmin() && hasPermission('org:settings:manage');

  const [orgName, setOrgName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Notification preferences — live query
  const [notifSavedMessage, setNotifSavedMessage] = useState('');
  const { data: notificationPrefs } = useQuery<NotificationPreferences>({
    queryKey: ['notification-preferences'],
    queryFn: () => organizationService.getNotificationPreferences(),
    enabled: !!user?.organizationId,
  });

  const notifMutation = useMutation({
    mutationFn: (data: Partial<NotificationPreferences>) =>
      organizationService.updateNotificationPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      setNotifSavedMessage('Notification preferences saved.');
      setTimeout(() => setNotifSavedMessage(''), 3000);
    },
  });

  const emailOnApproved = notificationPrefs?.emailOnApproved ?? true;
  const emailOnReport = notificationPrefs?.emailOnReport ?? true;
  const emailOnNewMember = notificationPrefs?.emailOnNewMember ?? true;

  const { data: organization, isLoading } = useQuery<Organization>({
    queryKey: ['my-organization'],
    queryFn: () => organizationService.getMyOrganization(),
    enabled: !!user?.organizationId,
  });

  useEffect(() => {
    if (organization) {
      setOrgName(organization.name || '');
    }
  }, [organization]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateOrganizationRequest) => organizationService.updateMyOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-organization'] });
      setSuccessMessage('Organization settings updated successfully');
      setErrorMessage('');
      setTimeout(() => setSuccessMessage(''), 5000);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Failed to update organization settings');
      setSuccessMessage('');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    updateMutation.mutate({ name: orgName });
  };

  if (isLoading) {
    return <LoadingState>Loading organization settings...</LoadingState>;
  }

  // Primary contact — use the current user's email as the org contact display
  const primaryContact = user?.email ?? '-';

  return (
    <Section>
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      {/* Organization Details */}
      <ContentCard padding="lg">
        <CardTitle>Organization Details</CardTitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            {canEdit ? (
              <Input
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Enter organization name"
                fullWidth
                required
              />
            ) : (
              <ReadOnlyField>
                <FieldLabel>Organization Name</FieldLabel>
                <FieldValue>{organization?.name || '-'}</FieldValue>
              </ReadOnlyField>
            )}
          </FormGroup>

          <ReadOnlyField>
            <FieldLabel>Primary Contact</FieldLabel>
            <FieldValue>{primaryContact}</FieldValue>
          </ReadOnlyField>

          {canEdit && (
            <ButtonGroup>
              <Button
                type="submit"
                variant="primary-blue"
                leftIcon={<Save />}
                isLoading={updateMutation.isPending}
                disabled={updateMutation.isPending}
              >
                Save Changes
              </Button>
            </ButtonGroup>
          )}
        </Form>
      </ContentCard>

      {/* Notification Defaults */}
      <ContentCard padding="lg">
        <CardTitle>Notification Defaults</CardTitle>
        <ToggleSwitch
          label="Email on submission approved"
          value={emailOnApproved}
          onChange={(val) => notifMutation.mutate({ emailOnApproved: val })}
          disabled={!canEditNotifications || notifMutation.isPending}
        />
        <ToggleSwitch
          label="Email on report ready"
          value={emailOnReport}
          onChange={(val) => notifMutation.mutate({ emailOnReport: val })}
          disabled={!canEditNotifications || notifMutation.isPending}
        />
        <ToggleSwitch
          label="Email on new member joined"
          value={emailOnNewMember}
          onChange={(val) => notifMutation.mutate({ emailOnNewMember: val })}
          disabled={!canEditNotifications || notifMutation.isPending}
        />
        {notifSavedMessage && <SuccessMessage>{notifSavedMessage}</SuccessMessage>}
      </ContentCard>
    </Section>
  );
};

export default GeneralSettingsTab;
